import React, {useMemo} from 'react';
import type {EBirdMyDataSchema} from "ebird-mydata-reader";
import {pluralize} from "../../../../helpers/helpers";
import {BASE_MONTHS} from "../../../../helpers/months";
import {MONTHLY_SIGHTINGS_TITLE, RECENT_YEARS_COUNT} from "./constants";
import {getSightingIntensity, getSpeciesSightingSummary, getSpeciesSightingWeeks} from "./helpers";
import './monthly-sightings-chart.scss';

export type MonthlySightingsChartProps = {
    observations: EBirdMyDataSchema[]
}

export function MonthlySightingsChart({observations}: MonthlySightingsChartProps) {
    const weeks = useMemo(() => getSpeciesSightingWeeks(observations), [observations]);
    const sightingSummary = useMemo(() => getSpeciesSightingSummary(observations), [observations]);
    const observationCount = sightingSummary.observationCount;
    const maxObservationCount = Math.max(1, ...weeks.map(week => week.observationCount));

    return (
        <section className="checklist__monthly-sightings" aria-label="Monthly sighting summary">
            <div className="checklist__monthly-sightings-header">
                <h3>{MONTHLY_SIGHTINGS_TITLE}</h3>
                <p>
                    {observationCount.toLocaleString()} {pluralize(observationCount, "observation")} across {sightingSummary.yearSpan} {pluralize(sightingSummary.yearSpan, "year")}.{' '}
                    {sightingSummary.recentYearsSeen > 0 ?
                        <>Seen {sightingSummary.recentYearsSeen} of the last {RECENT_YEARS_COUNT} years.</>
                        :
                        <>Last seen {sightingSummary.yearsSinceLastSighting} {pluralize(sightingSummary.yearsSinceLastSighting, "year")} ago.</>
                    }
                </p>
            </div>
            <div className="checklist__monthly-sightings-heatmap" role="img" aria-label="All-time species sighting activity by month and week">
                <div className="checklist__monthly-sightings-months" aria-hidden="true">
                    {BASE_MONTHS.map(month => <span key={month.value}>{month.label.slice(0, 3)}</span>)}
                </div>
                <div className="checklist__monthly-sightings-weeks">
                    {weeks.map(week => <span
                        key={`${week.month}-${week.week}`}
                        className={week.observationCount ? 'has-activity' : ''}
                        style={week.observationCount ? ({'--sighting-intensity': getSightingIntensity(week.observationCount, maxObservationCount)} as React.CSSProperties) : undefined}
                        title={`${BASE_MONTHS[week.month - 1].label}, week ${week.week}: ${week.observationCount} ${pluralize(week.observationCount, "observation")}`}
                    />)}
                </div>
                <div className="checklist__monthly-sightings-legend" aria-hidden="true">
                    <span>Less activity</span><i /><i /><i /><i /><span>More activity</span>
                </div>
            </div>
        </section>
    );
}
